import { ActionIcon, Alert, Badge, Button, Card, FileInput, Grid, Group, Modal, NumberInput, Select, SimpleGrid, Stack, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCube, IconDownload, IconRefresh, IconScan, IconTrash, IconUpload } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { downloadFile, http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Chart } from "../../components/Charts";
import { DataTable } from "../../components/DataTable";
import { PointCloudViewer } from "../../components/PointCloudViewer";
import { TreeCardDrawer } from "../../components/TreeCard";
import { Empty, fmtDate, fmtNum, KpiGrid, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";

interface Stat { n: number; min: number; p25: number; median: number; p75: number; max: number; mean: number; cv_pct: number | null }
interface Metrics {
  trees: number; status: Record<string, number>; density_per_ha: number | null; canopy_volume_m3_ha: number | null;
  summary: Record<string, Stat | null>; items: Record<string, unknown>[];
}
export interface PointCloud {
  id: number; survey_date: string; filename: string; source_srs: string | null; status: string; point_count: number | null;
  stats: Record<string, unknown> | null; error: string | null; created_by: string; created_at: string; has_viewer: boolean;
}

function histogram(vals: number[], bins = 12) {
  if (!vals.length) return [];
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const w = (hi - lo) / bins || 1;
  const out = Array.from({ length: bins }, (_, i) => ({ od: lo + i * w, n: 0 }));
  vals.forEach((v) => { out[Math.min(bins - 1, Math.floor((v - lo) / w))].n++; });
  return out.map((b) => ({ raspon: `${fmtNum(b.od, 1)}–${fmtNum(b.od + w, 1)}`, n: b.n }));
}

/** Stabla voćnjaka: metrika (iz 3D snimka / UAV), raspodjele, 3D snimci i 3D pregled. */
export function TreesTab({ orchardId, openUpload, siteId }: { orchardId: number; openUpload?: number; siteId?: number | null }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [tree, setTree] = useState<number | null>(null);
  const [view3d, setView3d] = useState<number | null>(null);
  const [upload, setUpload] = useState(false);
  useEffect(() => { if (openUpload) setUpload(true); }, [openUpload]);
  const [hist, setHist] = useState("height_m");
  const m = useQuery({ queryKey: ["tree-metrics", orchardId], queryFn: () => http.get<Metrics>(`/api/v1/orchards/${orchardId}/tree-metrics`) });
  const pcs = useQuery({ queryKey: ["pointclouds", orchardId], enabled: can("pointcloud:read"),
    queryFn: () => http.get<PointCloud[]>(`/api/v1/orchards/${orchardId}/pointclouds`),
    refetchInterval: (q) => ((q.state.data ?? []).some((p) => ["uploaded", "processing"].includes(p.status)) ? 5000 : false) });
  const ready = (pcs.data ?? []).filter((p) => p.status === "ready" && p.has_viewer);
  // kad obrada oblaka tačaka završi, metrika stabala (3D) i zaglavlje voćnjaka se osvježavaju bez ponovnog učitavanja strane
  const busyPc = (pcs.data ?? []).some((p) => ["uploaded", "processing"].includes(p.status));
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !busyPc) {
      qc.invalidateQueries({ queryKey: ["tree-metrics", orchardId] });
      qc.invalidateQueries({ queryKey: ["orchard"] });
    }
    wasBusy.current = busyPc;
  }, [busyPc, orchardId, qc]);
  const h = useMemo(() => histogram((m.data?.items ?? []).map((r) => Number(r[hist])).filter((v) => Number.isFinite(v) && v > 0)), [m.data, hist]);
  const s = m.data?.summary ?? {};

  async function del(id: number) {
    await http.del(`/api/v1/pointclouds/${id}`);
    qc.invalidateQueries({ queryKey: ["pointclouds", orchardId] });
  }
  async function reprocess(id: number) {
    await http.post(`/api/v1/pointclouds/${id}/reprocess`, {});
    qc.invalidateQueries({ queryKey: ["pointclouds", orchardId] });
  }

  return (
    <Stack>
      <Group justify="flex-end" gap="xs">
        <Button size="xs" variant="default" leftSection={<IconDownload size={14} />} onClick={() => fieldList(orchardId)}>{t("Lista stabala za teren (CSV)")}</Button>
        {can("orchards:write") && <UavDetect orchardId={orchardId} siteId={siteId} />}
      </Group>
      <KpiGrid items={[
        { label: "Stabala", value: fmtNum(m.data?.trees, 0), hint: m.data ? Object.entries(m.data.status).map(([k, v]) => `${t(k)}: ${v}`).join(" · ") : undefined },
        { label: "Gustina sklopa", value: fmtNum(m.data?.density_per_ha, 0), unit: t("stabala/ha") },
        { label: "Visina (medijan)", value: fmtNum(s.height_m?.median, 2), unit: "m", hint: s.height_m ? `${fmtNum(s.height_m.p25, 2)}–${fmtNum(s.height_m.p75, 2)} m · CV ${fmtNum(s.height_m.cv_pct, 1)}%` : undefined },
        { label: "Zapremina krošnji", value: fmtNum(m.data?.canopy_volume_m3_ha, 0), unit: "m³/ha", color: "soil",
          hint: s.crown_diameter_m ? `${t("prečnik krošnje")} ${fmtNum(s.crown_diameter_m.median, 2)} m` : undefined },
      ]} />

      {can("pointcloud:read") && (
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Title order={5}>{t("3D snimci (LAS/LAZ)")}</Title>
            <Group gap="xs">
              {ready.length > 0 && <Button size="xs" leftSection={<IconCube size={14} />} onClick={() => setView3d(view3d ? null : ready[0].id)}>
                {view3d ? t("Zatvori 3D") : t("3D pregled")}</Button>}
              {can("pointcloud:upload") && <Button size="xs" variant="light" leftSection={<IconUpload size={14} />} onClick={() => setUpload(true)}>{t("Učitaj snimak")}</Button>}
            </Group>
          </Group>
          {!(pcs.data ?? []).length ? <Empty text="Nema 3D snimaka za ovaj voćnjak" /> : (
            <DataTable rows={pcs.data as unknown as Record<string, unknown>[]} search={false} dense columns={[
              { key: "survey_date", label: "Datum snimanja", type: "date" }, { key: "filename", label: "Fajl" },
              { key: "source_srs", label: "Ulazni SRS", render: (r) => String(r.source_srs ?? (r.stats as Record<string, unknown> | null)?.source_srs ?? "auto") },
              { key: "point_count", label: "Tačaka", type: "integer" },
              { key: "trees", label: "Stabala", render: (r) => fmtNum((r.stats as Record<string, unknown> | null)?.trees_detected, 0) },
              { key: "status", label: "Status", render: (r) => <Tooltip label={String(r.error ?? "")} disabled={!r.error}><span><StatusBadge status={String(r.status)} /></span></Tooltip> },
              { key: "a", label: "", render: (r) => (
                <Group gap={4} wrap="nowrap">
                  {r.status === "ready" && <ActionIcon size="sm" variant="subtle" onClick={() => setView3d(Number(r.id))}><IconCube size={14} /></ActionIcon>}
                  {r.status === "ready" && <Tooltip label={t("Klasifikovan oblak (COPC)")}><ActionIcon size="sm" variant="subtle"
                    onClick={() => downloadFile(`/api/v1/pointclouds/${r.id}/download?what=copc`)}><IconDownload size={14} /></ActionIcon></Tooltip>}
                  {can("pointcloud:upload") && <Tooltip label={t("Ponovna obrada")}><ActionIcon size="sm" variant="subtle" onClick={() => reprocess(Number(r.id))}><IconRefresh size={14} /></ActionIcon></Tooltip>}
                  {can("pointcloud:upload") && <ActionIcon size="sm" variant="subtle" color="red" onClick={() => del(Number(r.id))}><IconTrash size={14} /></ActionIcon>}
                </Group>) },
            ]} />
          )}
          {view3d && (
            <div style={{ marginTop: 12, height: 620, borderRadius: 8, overflow: "hidden" }}>
              <PointCloudViewer pcId={view3d} onTree={(id) => setTree(id)} selectedTree={tree} />
            </div>
          )}
        </Card>
      )}

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card withBorder h="100%">
            <Group justify="space-between" mb="xs">
              <Title order={5}>{t("Raspodjela")}</Title>
              <Select size="xs" w={190} value={hist} onChange={(v) => v && setHist(v)} data={[
                { value: "height_m", label: t("Visina (m)") }, { value: "crown_diameter_m", label: t("Prečnik krošnje (m)") },
                { value: "crown_volume_m3", label: t("Zapremina krošnje (m³)") }, { value: "fruit_mm", label: t("Prečnik ploda (mm)") }]} />
            </Group>
            {h.length ? <Chart rows={h} x="raspon" series={[{ key: "n", label: t("Broj stabala") }]} height={240} /> : <Empty />}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card withBorder h="100%">
            <Title order={5} mb="xs">{t("Sažetak metrike")}</Title>
            <DataTable search={false} dense rows={Object.entries(s).filter(([, v]) => v).map(([k, v]) => ({ k: t(LABELS[k] ?? k), ...v! })) as unknown as Record<string, unknown>[]}
              columns={[{ key: "k", label: "Pokazatelj" }, { key: "n", label: "n", type: "integer" }, { key: "min", label: "Min", type: "number" },
                { key: "p25", label: "P25", type: "number" }, { key: "median", label: "Medijan", type: "number" }, { key: "p75", label: "P75", type: "number" },
                { key: "max", label: "Max", type: "number" }, { key: "cv_pct", label: "CV %", type: "number", decimals: 1 }]} />
          </Card>
        </Grid.Col>
      </Grid>

      <Card withBorder>
        <Title order={5} mb="xs">{t("Stabla")}</Title>
        <DataTable rows={m.data?.items ?? []} exportName={`stabla_${orchardId}`} maxHeight={520} onRowClick={(r) => setTree(Number(r.id))} columns={[
          { key: "code", label: "Šifra" }, { key: "row_no", label: "Red", type: "integer" }, { key: "position_no", label: "Poz.", type: "integer" },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
          { key: "height_m", label: "Visina (m)", type: "number" }, { key: "crown_diameter_m", label: "Krošnja (m)", type: "number" },
          { key: "crown_area_m2", label: "Površina (m²)", type: "number" }, { key: "crown_volume_m3", label: "Zapremina (m³)", type: "number" },
          { key: "source", label: "Izvor" }, { key: "measured_on", label: "Mjereno", type: "date" },
          { key: "photos", label: "Foto", type: "integer" }, { key: "fruit_mm", label: "Plod ⌀ (mm)", type: "number", decimals: 1 },
        ]} />
      </Card>
      <TreeCardDrawer treeId={tree} onClose={() => setTree(null)} />
      <UploadModal opened={upload} onClose={() => setUpload(false)} orchardId={orchardId}
        onDone={() => qc.invalidateQueries({ queryKey: ["pointclouds", orchardId] })} />
    </Stack>
  );
}

const LABELS: Record<string, string> = {
  height_m: "Visina (m)", crown_diameter_m: "Prečnik krošnje (m)", crown_area_m2: "Površina krošnje (m²)",
  crown_volume_m3: "Zapremina krošnje (m³)", trunk_diameter_cm: "Prečnik debla (cm)",
};

/** Probni snimci za vježbu uvoza (prikazuju se samo ako ih administrator postavi u skladište samples/). */
function SampleFiles({ enabled }: { enabled: boolean }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["pc-samples"], enabled, retry: false,
    queryFn: () => http.get<{ name: string; size_bytes: number; srs: string; description: string }[]>("/api/v1/pointclouds/samples") });
  if (!q.data?.length) return null;
  return (
    <Card withBorder radius="md" p="sm" bg="var(--mantine-color-agri-light)">
      <Text size="sm" fw={700}>{t("Nemate snimak? Preuzmite testni")}</Text>
      <Text size="xs" c="dimmed" mb={6}>{t(q.data[0].description)}</Text>
      <Group gap="xs">
        {q.data.map((f) => (
          <Button key={f.name} size="xs" variant="white" leftSection={<IconDownload size={14} />}
            onClick={() => downloadFile(`/api/v1/pointclouds/samples/${f.name}`).catch((e) => notifications.show({ color: "red", message: String(e) }))}>
            {f.srs} · {(f.size_bytes / 1e6).toFixed(1)} MB
          </Button>
        ))}
      </Group>
    </Card>
  );
}

function UploadModal({ opened, onClose, orchardId, onDone }: { opened: boolean; onClose: () => void; orchardId: number; onDone: () => void }) {
  const { t } = useT();
  const [file, setFile] = useState<File | null>(null);
  const [date, setDate] = useState<string | null>(new Date().toISOString().slice(0, 10));
  const [srs, setSrs] = useState<string | null>("");
  const [busy, setBusy] = useState(false);
  const opts = useQuery({ queryKey: ["pc-srs"], enabled: opened, queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/pointclouds/srs") });
  async function send() {
    if (!file || !date) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("survey_date", date);
      if (srs) fd.append("source_srs", srs);
      await http.form(`/api/v1/orchards/${orchardId}/pointclouds`, fd);
      notifications.show({ color: "green", message: t("Snimak je učitan — obrada je pokrenuta (klasifikacija, stabla, metrika).") });
      onDone(); onClose(); setFile(null);
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    } finally { setBusy(false); }
  }
  return (
    <Modal opened={opened} onClose={onClose} title={t("Učitavanje 3D snimka voćnjaka")} size="lg">
      <Stack>
        <FileInput label={t("LAS ili LAZ fajl")} accept=".las,.laz" value={file} onChange={setFile} required clearable
          description={t("UAV LiDAR ili fotogrametrijski oblak tačaka; do 4 GB.")} />
        <SimpleGrid cols={2}>
          <TextInput type="date" label={t("Datum snimanja")} value={date ?? ""} onChange={(e) => setDate(e.currentTarget.value)} required />
          <Select label={t("Koordinatni sistem snimka")} value={srs} onChange={setSrs}
            data={[{ value: "", label: t("Automatski (iz zaglavlja)") }, ...(opts.data ?? []).map((o) => ({ value: o.code, label: `${o.code} — ${t(o.name)}` }))]} />
        </SimpleGrid>
        <Alert color="blue" variant="light">
          {t("Podržano: WGS84 (EPSG:4326), UTM 33N/34N, EPSG:8677 i 8678. Ako zaglavlje nema koordinatni sistem, prepoznaje se po opsegu koordinata; izlaz je uvijek EPSG:8678. Obrada: tlo (SMRF), vegetacija po visini, izdvajanje stabala i metrika po stablu.")}
        </Alert>
        <SampleFiles enabled={opened} />
        <Group justify="flex-end">
          <Badge variant="light" color="gray">{file ? `${(file.size / 1e6).toFixed(1)} MB` : ""}</Badge>
          <Button loading={busy} disabled={!file || !date} onClick={send} leftSection={<IconUpload size={16} />}>{t("Učitaj i obradi")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Prirast ploda u voćnjaku: prosjek mjerenih stabala kroz sezonu (+ fenološka zapažanja prečnika). */
export function GrowthTab({ orchardId, season }: { orchardId: number; season: number }) {
  const { t } = useT();
  const [tree, setTree] = useState<number | null>(null);
  const g = useQuery({ queryKey: ["fruit-growth", orchardId, season],
    queryFn: () => http.get<{ photos: { datum: string; mean_mm: number; stabala: number; plodova: number; sd_mm: number | null }[];
      phenology: { datum: string; bbch_code: number; mean_mm: number }[] }>(`/api/v1/orchards/${orchardId}/fruit-growth`, { season }) });
  const m = useQuery({ queryKey: ["tree-metrics", orchardId], queryFn: () => http.get<Metrics>(`/api/v1/orchards/${orchardId}/tree-metrics`) });
  const withPhotos = (m.data?.items ?? []).filter((r) => Number(r.photos) > 0);
  const series = (g.data?.photos ?? []).map((r, i, a) => ({ ...r,
    prirast: i ? (r.mean_mm - a[i - 1].mean_mm) / Math.max(1, (new Date(r.datum).getTime() - new Date(a[i - 1].datum).getTime()) / 86400000) : null }));
  return (
    <Stack>
      <Card withBorder>
        <Title order={5} mb="xs">{t(`Prirast ploda — sezona ${season}`)}</Title>
        {!series.length ? <Empty text="Nema mjerenja plodova u sezoni — fotografišite plodove uz referentnu karticu i izmjerite ih u kartonu stabla" /> : (
          <>
            <Chart type="line" rows={series} x="datum" xLabel={(v) => fmtDate(v)} series={[{ key: "mean_mm", label: t("Prosječan prečnik (mm)") }]} />
            <DataTable rows={series as unknown as Record<string, unknown>[]} search={false} dense columns={[
              { key: "datum", label: "Datum", type: "date" }, { key: "stabala", label: "Stabala", type: "integer" },
              { key: "plodova", label: "Plodova", type: "integer" }, { key: "mean_mm", label: "Prosjek (mm)", type: "number", decimals: 1 },
              { key: "sd_mm", label: "SD (mm)", type: "number", decimals: 1 }, { key: "prirast", label: "Prirast (mm/dan)", type: "number" },
            ]} />
          </>
        )}
      </Card>
      <Card withBorder>
        <Title order={5} mb="xs">{t("Stabla sa fotografijama")}</Title>
        {!withPhotos.length ? <Empty text="Nema fotografija" /> : (
          <DataTable rows={withPhotos} search={false} onRowClick={(r) => setTree(Number(r.id))} columns={[
            { key: "code", label: "Stablo" }, { key: "row_no", label: "Red", type: "integer" }, { key: "photos", label: "Fotografija", type: "integer" },
            { key: "fruit_mm", label: "Posljednji ⌀ (mm)", type: "number", decimals: 1 }, { key: "height_m", label: "Visina (m)", type: "number" },
          ]} />
        )}
        <Text size="xs" c="dimmed" mt="xs">{t("Fotografije se dodaju u kartonu stabla (klik na stablo u tabeli, na mapi ili u 3D pregledu) ili ZIP uvozom.")}</Text>
      </Card>
      <TreeCardDrawer treeId={tree} onClose={() => setTree(null)} />
    </Stack>
  );
}


async function fieldList(orchardId: number) {
  const rows = await http.get<Record<string, unknown>[]>(`/api/v1/orchards/${orchardId}/field-list`);
  const head = ["code", "status", "lat", "lon", "height_m", "ndvi", "ndre"];
  const csv = [head.join(";"), ...rows.map((r) => head.map((h) => String(r[h] ?? "")).join(";"))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
  a.download = `teren_vocnjak_${orchardId}.csv`;
  a.click();
}

/** Izdvajanje stabala iz UAV nDSM-a (isti algoritam kao za 3D; trajni ID stabala se zadržava). */
function UavDetect({ orchardId, siteId }: { orchardId: number; siteId?: number | null }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [survey, setSurvey] = useState<string | null>(null);
  const [minH, setMinH] = useState<number | string>(1.0);
  const [busy, setBusy] = useState(false);
  const surveys = useQuery({ queryKey: ["surveys"], enabled: open,
    queryFn: () => http.get<{ id: number; site_id: number; survey_date: string; assets: { kind: string; status: string }[] }[]>("/api/v1/uav/surveys") });
  const opts = (surveys.data ?? []).filter((s) => !siteId || s.site_id === siteId)
    .filter((s) => s.assets.some((a) => a.kind === "ndsm" && a.status === "ready"));
  async function run() {
    if (!survey) return;
    setBusy(true);
    try {
      const r = await http.post<{ job_id: number }>(`/api/v1/orchards/${orchardId}/detect`, { survey_id: Number(survey), min_height_m: Number(minH) });
      for (let i = 0; i < 150; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const j = await http.get<{ status: string; result: { trees?: number; new?: number; matched?: number }; error?: string }>(`/api/v1/uav/jobs/${r.job_id}`);
        if (j.status === "done") {
          notifications.show({ color: "green", title: t("Detekcija završena"), message: `${j.result.trees} ${t("stabala")} · ${t("nova")}: ${j.result.new} · ${t("uparena")}: ${j.result.matched}` });
          break;
        }
        if (j.status === "failed") throw new Error(j.error?.split("\n").filter(Boolean).pop());
      }
      // nova stabla mijenjaju i zaglavlje voćnjaka (broj stabala, gustina), slojeve i 3D metriku — osvježava se sve
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    } finally { setBusy(false); }
  }
  return (
    <>
      <Button size="xs" variant="default" leftSection={<IconScan size={14} />} onClick={() => setOpen(true)}>{t("Detekcija iz UAV snimka")}</Button>
      <Modal opened={open} onClose={() => setOpen(false)} title={t("Izdvajanje stabala iz UAV snimka (nDSM)")}>
        <Stack>
          <Select label={t("UAV snimanje")} data={opts.map((s) => ({ value: String(s.id), label: s.survey_date }))} value={survey} onChange={setSurvey}
            nothingFoundMessage={t("Nema obrađenog nDSM-a za lokalitet ovog voćnjaka")} />
          <NumberInput label={t("Minimalna visina stabla (m)")} value={minH} onChange={setMinH} min={0.3} max={5} step={0.1} decimalScale={1} />
          <Group justify="flex-end"><Button loading={busy} disabled={!survey} onClick={run}>{t("Pokreni detekciju")}</Button></Group>
        </Stack>
      </Modal>
    </>
  );
}
